// Mochi's sky-peek: browser Geolocation + Open-Meteo current-weather.
// Open-Meteo needs no API key. We cache the result in localStorage for 1 hour
// so repeat visits and re-opens don't spam the network.
//
// Permission flow is opt-in: we never silently call navigator.geolocation —
// the user explicitly accepts via the Morning Check-In sky-peek prompt.
//
//   const w = await requestWeatherPermissionAndFetch();
//   // → { condition, season, hemisphere, temperature_c, raw } or null

const CACHE_KEY = "mochi-weather-cache";
const PERMISSION_KEY = "mochi-weather-permission"; // "granted" | "denied" | null
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

// WMO weather codes → simplified Mochi conditions.
// Reference: https://open-meteo.com/en/docs (weathercode interpretation table)
function wmoToCondition(code) {
    if (code == null) return "cloudy";
    // 0 clear, 1 mainly clear → sunny
    if (code === 0 || code === 1) return "sunny";
    // 2/3 partly/overcast → cloudy
    if (code === 2 || code === 3) return "cloudy";
    // 45/48 fog → cloudy
    if (code === 45 || code === 48) return "cloudy";
    // 51..67 drizzle/rain → rainy
    if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return "rainy";
    // 71..77 snow + 85/86 snow showers → snowy
    if ((code >= 71 && code <= 77) || code === 85 || code === 86) return "snowy";
    // 95..99 thunderstorm → rainy (close enough for Mochi flavor)
    if (code >= 95 && code <= 99) return "rainy";
    return "cloudy";
}

// Northern: Dec/Jan/Feb=winter, Mar/Apr/May=spring, Jun/Jul/Aug=summer, Sep/Oct/Nov=autumn.
// Southern hemisphere: flipped by 6 months.
function monthToSeason(monthIdx0, hemisphere) {
    // monthIdx0 is 0..11
    const m = monthIdx0;
    const isSouth = hemisphere === "south";
    const norm = isSouth ? (m + 6) % 12 : m;
    if (norm === 11 || norm === 0 || norm === 1) return "winter";
    if (norm >= 2 && norm <= 4) return "spring";
    if (norm >= 5 && norm <= 7) return "summer";
    return "autumn";
}

export function getCachedWeather() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.fetched_at) return null;
        if (Date.now() - parsed.fetched_at > CACHE_TTL_MS) return null;
        return parsed.data;
    } catch {
        return null;
    }
}

function setCachedWeather(data) {
    try {
        localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({ fetched_at: Date.now(), data }),
        );
    } catch (e) { console.warn("weather cache write failed (storage quota?)", e); }
}

export function getPermissionState() {
    try {
        return localStorage.getItem(PERMISSION_KEY); // "granted" | "denied" | null
    } catch {
        return null;
    }
}

export function setPermissionState(state) {
    try {
        localStorage.setItem(PERMISSION_KEY, state);
    } catch (e) { console.warn("weather permission persist failed", e); }
}

// Returns Promise<{lat, lon}> or rejects.
function getBrowserCoords() {
    return new Promise((resolve, reject) => {
        if (!("geolocation" in navigator)) {
            reject(new Error("geolocation_unsupported"));
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
            (err) => reject(err),
            { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
        );
    });
}

async function fetchOpenMeteo(lat, lon) {
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`open_meteo_${res.status}`);
    const json = await res.json();
    return json.current_weather; // { temperature, weathercode, windspeed, time }
}

function buildWeatherSnapshot({ lat, current }) {
    const hemisphere = lat >= 0 ? "north" : "south";
    const condition = wmoToCondition(current?.weathercode);
    const season = monthToSeason(new Date().getMonth(), hemisphere);
    return {
        condition,
        season,
        hemisphere,
        temperature_c: current?.temperature ?? null,
        weathercode: current?.weathercode ?? null,
    };
}

// User-gesture entry point — only call this from inside a click/tap handler.
// Resolves to a weather snapshot if permission is granted, otherwise null.
export async function requestWeatherPermissionAndFetch() {
    try {
        const { lat, lon } = await getBrowserCoords();
        setPermissionState("granted");
        const current = await fetchOpenMeteo(lat, lon);
        const snap = buildWeatherSnapshot({ lat, current });
        setCachedWeather(snap);
        return snap;
    } catch (err) {
        // PERMISSION_DENIED is code 1; treat all rejections as soft-denied for UX.
        if (err && err.code === 1) {
            setPermissionState("denied");
        }
        return null;
    }
}

// Read-only "get whatever is cached or null" helper for non-gesture contexts
// (idle bubble, chat system prompt, etc.). Never prompts the browser.
export function getWeatherSnapshot() {
    return getCachedWeather();
}

// Refresh in the background if the cache is stale AND permission was previously
// granted. Silent — does not re-prompt. Returns the (possibly updated) snapshot.
export async function refreshWeatherIfPermitted() {
    if (getPermissionState() !== "granted") return getCachedWeather();
    const cached = getCachedWeather();
    if (cached) return cached;
    try {
        const { lat, lon } = await getBrowserCoords();
        const current = await fetchOpenMeteo(lat, lon);
        const snap = buildWeatherSnapshot({ lat, current });
        setCachedWeather(snap);
        return snap;
    } catch (e) {
        console.warn("background weather refresh failed — serving cached snapshot", e);
        return cached;
    }
}

// Exposed for tests / debug only.
export const __internals = { wmoToCondition, monthToSeason };
